/* @flow */

import React, { Component } from 'react'
import { connect } from 'react-redux'
import type { Crop } from '../../ImageAsBlob'

import './RankingPollForm.css'

import Option from './Option'
import CropModal from './CropModal'
import IfAdmin from '../../IfAdmin'
import TagsSelect from '../TagsSelect'
import Controls from '../Controls'
import Condition from '../../Condition'
import PlusIcon from '../../Icons/PlusIcon'
import { getTags } from '../../../stores/tags'
import PollFormToggles from '../PollFormToggles'
import CustomerSelect from '../../CustomerSelect/'
import OpinaryInput from '../../Controls/OpinaryInput'

import { selectCurrentUser } from '../../../stores/currentUser'
import { pollBuilderSaveButtonClick } from '../../../sagas/savePoll'
import { generateEmptyRankingOption } from '../../../stores/pollsStore'
import { pollBuilderDeleteButtonClick } from '../../../sagas/deletePoll'
import type { CheckedoutPoll, RankingOption, Tag } from '../../../types'
import {
  newMinLengthValidator,
  newMaxLengthValidator,
  pollVerticalValidator,
} from '../../../helpers'
import {
  MIN_POLL_LABEL_LENGTH,
  MAX_POLL_LABEL_LENGTH,
} from '../../CheckedoutPoll'
import {
  selectCheckedoutPoll,
  updateCheckedoutPoll,
  uploadRankingToolImageStart,
} from '../../../stores/pollBuilderCheckedoutPoll'
import {
  selectCustomerById,
  selectCustomerVerticals,
} from '../../../stores/customersStore'

type File = {
  preview?: string,
}

type State = {
  file: ?File,
  crop?: Crop,
  blob: ?string,
  errors: boolean,
  optionIndex: number,
  horizontal: boolean,
  fileForOptionId: ?string,
}

type User = {
  id: number,
  name: string,
  email: string,
  password: string,
  customerId: string,
}

type Props = {
  user: User,
  tags: Array<Tag>,
  isAdmin: boolean,
  poll: CheckedoutPoll,
  deletePoll: () => void,
  toggleAttribute: Function,
  updatePoll: Object => void,
  savePoll: (poll: CheckedoutPoll) => void,
  uploadRankingOptionImage: (optionId: string, blob: string) => void,
  customerVerticals: Array<Object>,
  customerConfig: Object,
}

const horizontalCrop = {
  width: 170,
  height: 100,
  aspect: 1.5,
}

const verticalCrop = {
  width: 140,
  height: 190,
  aspect: 0.8,
}

export class RankingPollForm extends Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      file: null,
      blob: null,
      errors: false,
      optionIndex: 0,
      horizontal: false,
      fileForOptionId: null,
      crop: {
        y: 1,
        x: 25,
        ...verticalCrop,
      },
    }
  }

  updateAttr = (attrName: string, value: any) => {
    const { updatePoll, poll } = this.props

    updatePoll({
      ...poll,
      [attrName]: value,
    })
  }

  componentDidUpdate(prevProps: Object, prevState: Object) {
    const { poll } = this.props
    const { horizontal } = this.state

    if (
      poll.horizontal !== prevProps.poll.horizontal ||
      poll.horizontal !== prevState.horizontal
    ) {
      this.setState({ horizontal: !prevState.horizontal })

      if (horizontal === true) {
        this.setState({
          crop: { ...horizontalCrop },
        })
      } else if (horizontal === false) {
        this.setState({
          crop: { ...verticalCrop },
        })
      }
    }
  }

  makeChangeHandler(key: string) {
    return (event: Event) => {
      if (event.target.value == null) {
        throw new Error('expected event.target.value to exist')
      }
    }
  }

  setCustomerId = (customer: string) => {
    this.updateAttr('customer', customer)
  }

  setTitle = (header: string) => {
    this.updateAttr('header', header)
  }

  setName = (name: string) => {
    if (!name) {
      this.updateAttr('userEnteredId', false)
    } else {
      this.updateAttr('userEnteredId', true)
    }
    this.updateAttr('name', name)
  }

  getOrderedOptions = (): Array<RankingOption> => {
    if (!this.props.poll.options) return []
    return this.props.poll.options
  }

  setOptions = (options: Array<RankingOption>) => {
    this.updateAttr('options', options)
  }

  addEmptyOption = () => {
    const orderedOptions = this.getOrderedOptions()
    const opitonsPosition = orderedOptions.map(({ position }) => position)
    this.setOptions([
      ...orderedOptions,
      generateEmptyRankingOption(Math.max(...opitonsPosition) + 1),
    ])
  }

  removeOption = (index: number) => {
    const optionsCopy = [...this.getOrderedOptions()]
    optionsCopy.splice(index, 1)
    this.setOptions(optionsCopy)
  }

  removeOptionImage = (index: number) => {
    const optionsCopy = [...this.getOrderedOptions()]
    optionsCopy[index] = {
      ...optionsCopy[index],
      image: null,
    }
    this.setOptions(optionsCopy)
  }

  updateOption = (index: number) => (attrName: string) => (value: any) => {
    const orderedOptions = this.getOrderedOptions()
    const option = orderedOptions[index]

    orderedOptions[index] = {
      ...option,
      [attrName]: value,
    }

    this.setOptions(orderedOptions)
  }

  resetState = () => this.setState({ file: null, fileForOptionId: null })

  render() {
    const {
      poll,
      savePoll,
      deletePoll,
      isAdmin,
      tags,
      customerVerticals,
      customerConfig,
    } = this.props

    const orderedOptions = this.getOrderedOptions()

    const minError = newMinLengthValidator(MIN_POLL_LABEL_LENGTH)
    const maxError = newMaxLengthValidator(MAX_POLL_LABEL_LENGTH)
    const verticalsError = pollVerticalValidator(
      poll,
      customerVerticals,
      customerConfig.enableVerticalTargeting
    )
    const handlerBlur = event => {
      let name = event.target.name
      const { updatePoll } = this.props

      event.target.name && event.target.value
        ? updatePoll({ [name]: false })
        : updatePoll({ [name]: true })
    }

    const isFormValid = () => {
      return (
        verticalsError == null &&
        minError(poll.header) == null &&
        maxError(poll.header) == null
      )
    }

    if (!orderedOptions) return null

    const renderToggles = () => {
      return (
        <div className="flex flex-column ml-auto">
          <PollFormToggles
            poll={poll}
            toggleAttribute={this.updateAttr}
            isAdmin={isAdmin}
          />
        </div>
      )
    }

    return (
      <section className="ranking-poll flex flex-column items-center">
        {this.renderCropModal()}
        <div className="w-100 relative mb4 flex">
          <span className="bg-op-con-dark op-con-light f6 ph4 h3 flex items-center">
            <span className="nowrap">Multiple Choice</span>
          </span>
          <span className="flex-auto w-60 mh4">
            <IfAdmin>
              <CustomerSelect
                className="w-100 mt1 bb b--moon-gray"
                colorClass="gray"
                onChange={this.setCustomerId}
                customerId={poll.customer}
              />
            </IfAdmin>
          </span>
          <span className="f6 bg-near-white gray ph4 h3 flex items-center">
            <span>Example</span>
          </span>
        </div>

        <OpinaryInput
          id="name"
          inputClass="tc"
          value={poll.header}
          className="w-70 mb3"
          onBlur={handlerBlur}
          name="questionInput"
          placeholder="Question"
          maxLength={MAX_POLL_LABEL_LENGTH + 1}
          onChange={event => this.setTitle(event.target.value)}
          borderClass={maxError(poll.header) ? 'b--dark-red' : 'b--moon-gray'}
        />

        <Condition shouldRender={poll.questionInput ? true : false}>
          <div className="tl dark-red"> Field cannot be empty </div>
        </Condition>
        <Condition shouldRender={maxError(poll.header) != null}>
          <div className="tl dark-red">{maxError(poll.header)}</div>
        </Condition>

        <div className="w-90 flex justify-center">
          {orderedOptions.map(this.renderOption)}
          <div className="flex items-end relative">
            {orderedOptions.length < 7 ? (
              <PlusIcon
                className="pointer op-brand absolute bottom-0 mb4"
                style={{ right: '-35px' }}
                onClick={this.addEmptyOption}
                size={35}
              />
            ) : null}
          </div>
        </div>
        <div className="w-100 flex justify-center mb4">
          <div className="w-90 flex items-end">
            <div className="w-40">
              <label className="f6 gray db">Poll ID</label>
              {poll.id ? (
                <span className="gray mt1">{poll.name}</span>
              ) : (
                <OpinaryInput
                  id="name"
                  onChange={event => this.setName(event.target.value)}
                  value={poll.name}
                  className="w-100 f6"
                />
              )}

              <TagsSelect
                options={tags}
                className="mt4"
                value={poll.tags}
                onChange={val => this.updateAttr('tags', val)}
              />
            </div>
            <div className="w-40 ml-auto">{renderToggles()}</div>
          </div>
        </div>
        <div className="w-100 h3 bg-near-white flex items-center justify-center">
          <div className="w-90">
            <Controls
              poll={poll}
              savePoll={savePoll(poll)}
              deletePoll={deletePoll}
              isFormValid={isFormValid()}
            />
          </div>
        </div>
      </section>
    )
  }

  renderCropModal = () => {
    const { poll } = this.props

    const { optionIndex, file, fileForOptionId, crop, blob } = this.state

    if (!file || !fileForOptionId) return null

    return (
      <CropModal
        file={file}
        crop={crop}
        blob={blob}
        poll={poll}
        optionIndex={optionIndex}
        onClose={this.resetState}
        fileForOptionId={fileForOptionId}
        setBlob={blob => this.setState({ blob })}
        onComplete={crop => this.setState({ crop })}
        onImageLoaded={crop => this.setState({ crop })}
        uploadImage={blob => {
          this.props.uploadRankingOptionImage(fileForOptionId, blob)
        }}
        changePhotoCredit={e => {
          this.updateOption(optionIndex)('imageCredit')(e.target.value)
        }}
      />
    )
  }

  renderOption = (option: RankingOption, index: number) => {
    const { optionId } = option

    const onDrop = file => {
      this.setState({
        file: file,
        optionIndex: index,
        fileForOptionId: optionId,
      })
    }

    return (
      <div key={optionId} className="mb4 ml2 mr2" style={{ width: '300px' }}>
        <Option
          option={option}
          onDrop={onDrop}
          onRemove={() => this.removeOption(index)}
          onRemoveImage={() => this.removeOptionImage(index)}
          onNameChange={name => this.updateOption(index)('name')(name)}
        />
      </div>
    )
  }
}

const mapStateToProps = state => {
  const poll = selectCheckedoutPoll(state)
  return {
    tags: getTags(state),
    user: selectCurrentUser(state),
    customerVerticals: selectCustomerVerticals(state),
    customerConfig: poll ? selectCustomerById(state, poll.customer) : {},
  }
}

const mapDispatchToProps = (dispatch, ownPoll) => ({
  updatePoll(poll) {
    dispatch(updateCheckedoutPoll(poll))
  },
  uploadRankingOptionImage(...args) {
    dispatch(uploadRankingToolImageStart(...args))
  },
  deletePoll() {
    dispatch(pollBuilderDeleteButtonClick())
  },
  savePoll(poll) {
    return () => {
      dispatch(
        updateCheckedoutPoll({
          ...poll,
          options: poll.options,
        })
      )
      dispatch(pollBuilderSaveButtonClick())
    }
  },
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(RankingPollForm)



// WEBPACK FOOTER //
// ./src/components/PollForm/RankingPollForm/index.js